import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadJobSectorDetails } from '../../../store/directusService';
import {
    createParticipantJobGoal,
    deleteParticipantJobGoal,
    loadParticipantJobGoals,
    onDeleteJobGoal,
    updateParticipantJobGoal
} from '../../../store/participantService';
import { getConfiguredItems } from '../../../utils/directusFunctions';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { JOB_SECTOR_DETAILS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { hasRole, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import LoadingSpinner from '../../ui/LoadingSpinner';

import EditJobGoal from './EditJobGoal';
import JobGoalCard from './JobGoalCard';

import app from '../../../app.module.css';
import cards from '../../../commonStyles/cardContainer.module.css';
import local from './jobGoals.module.css';

const editAndDeleteRoles = [QUALITY, SUPERUSER];

const JobGoals = ({
    currentParticipant,
    isDisabledFromParent = false,
    isViewOnly = false,
    employabilityJobGoals,
    updateGoalsForEmployability,
    isEmployability = false
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [jobSectorsForContract, setJobSectorsForContract] = useState([]);
    const [goals, setGoals] = useState([]);
    const [currentJobGoal, setCurrentJobGoal] = useState({});
    const [editType, setEditType] = useState('create');
    const [isOpenEditor, setIsOpenEditor] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isDisabledEditAndDelete, setIsDisabledEditAndDelete] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const participantJobGoals = useSelector(
        (state) => state.entities.participantService.currentParticipantJobGoals
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        setJobSectorsForContract([]);
        if (jobSectorDetails?.length < 1) dispatch(loadJobSectorDetails());
    }, []);

    useEffect(() => {
        !isEmployability && dispatch(loadParticipantJobGoals(currentParticipant.id));
    }, [currentParticipant]);

    useEffect(() => {
        if (!currentParticipant?.id || roles.length < 1) return;
        const isDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
        setIsDisabled(isViewOnly || isDisabledFromParent || isDisabledOnClosedStatus);
        setIsDisabledEditAndDelete(isViewOnly || !hasRole(editAndDeleteRoles, roles) || isDisabledOnClosedStatus);
    }, [currentParticipant.id, roles]);

    useEffect(() => {
        if (jobSectorDetails?.length > 0 && jobSectorsForContract?.length < 1) {
            setJobSectorsForContract(
                getConfiguredItems(jobSectorDetails, currentParticipant?.contractId)
            );
        }
    }, [jobSectorDetails]);

    useEffect(() => {
        if (isEmployability) setGoals(employabilityJobGoals);
        else setGoals(participantJobGoals);
    }, [employabilityJobGoals, participantJobGoals]);

    useEffect(() => {
        if (successMessage === `Job goal ${currentJobGoal?.title} has been deleted`) {
            dispatch(onDeleteJobGoal(currentJobGoal.id));
            setCurrentJobGoal({});
        }
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (jobSectorsForContract?.length < 1) return JOB_SECTOR_DETAILS_ERROR;
    };

    // EVENTHANDLERS
    const onAddJobGoal = () => {
        setEditType('create');
        setIsOpenEditor(true);
    };

    const onEditGoal = (jobGoal) => {
        setCurrentJobGoal(jobGoal);
        setEditType('edit');
        setIsOpenEditor(true);
    };

    const addJobGoal = (newJobGoal) => {
        setIsOpenEditor(false);
        const updatedGoals = [...goals, newJobGoal];
        setGoals(updatedGoals);
        if (isEmployability) updateGoalsForEmployability(updatedGoals);
        else {
            const payload = { ...newJobGoal, id: null };
            dispatch(
                createParticipantJobGoal(payload, `Job goal ${newJobGoal?.title} has been created`)
            );
        }
    };

    const deleteGoal = (jobGoal) => {
        setCurrentJobGoal(jobGoal);
        const updatedGoals = goals.filter((el) => el.id !== jobGoal.id);
        setGoals(updatedGoals);
        if (isEmployability) updateGoalsForEmployability(updatedGoals);
        else {
            dispatch(
                deleteParticipantJobGoal(jobGoal.id, `Job goal ${jobGoal?.title} has been deleted`)
            );
        }
    };

    const updateJobGoal = (updatedGoal) => {
        setIsOpenEditor(false);
        setCurrentJobGoal({});
        const newGoals = goals.map((el) => (el.id === updatedGoal.id ? updatedGoal : el));
        setGoals(newGoals);
        if (isEmployability) updateGoalsForEmployability(newGoals);
        else {
            dispatch(
                updateParticipantJobGoal(
                    updatedGoal,
                    `Job goal ${updatedGoal?.title} has been updated`
                )
            );
        }
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div className={cards.cardContainer} data-testid="job_goals">
            <h2 className={app.sectionHeading}>Job Goals</h2>

            <div className={cards.cards}>
                {goals?.map((el) => (
                    <div key={el.id}>
                        <JobGoalCard
                            jobGoal={el}
                            isDisabled={isDisabledEditAndDelete}
                            onEditJobGoal={onEditGoal}
                            onDeleteJobGoal={deleteGoal}
                        />
                    </div>
                ))}
                {isOpenEditor && (
                    <EditJobGoal
                        jobGoal={currentJobGoal}
                        participantId={currentParticipant.id}
                        jobSectors={jobSectorsForContract}
                        isDisabled={
                            editType === 'edit' ? isDisabledEditAndDelete : isDisabled
                        }
                        editType={editType}
                        isOpen={isOpenEditor}
                        onCancel={() => setIsOpenEditor(false)}
                        onUpdate={updateJobGoal}
                        onAdd={addJobGoal}
                    />
                )}
            </div>
            <div className={local.jobgoalsActions}>
                <Button
                    id="AddJobGoalCard"
                    type="button"
                    onClick={onAddJobGoal}
                    disabled={isDisabled}
                    content="Add a Job Goal"
                />
            </div>
        </div>
    );
};

JobGoals.propTypes = {
    currentParticipant: PropTypes.object,
    isDisabledFromParent: PropTypes.bool,
    isViewOnly: PropTypes.bool,
    employabilityJobGoals: PropTypes.arrayOf(PropTypes.object),
    updateGoalsForEmployability: PropTypes.func,
    isEmployability: PropTypes.bool
};

export default JobGoals;
